import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import PropTypes from "prop-types";
import { sanitizeHtml } from "./sanitize";

// Contenteditable WYSIWYG input for chat composer
const RichTextInput = forwardRef(function RichTextInput({
  valueHtml = "",
  onChangeHtml,
  placeholder = "",
  className = "",
  onKeyDown,
  onKeyUp,
  onFocus,
  onClick,
  onBlur,
  onPaste,
  disabled = false,
  ariaLabel,
}, ref) {
  const elRef = useRef(null);

  const syncFromProp = useCallback(() => {
    const el = elRef.current; if (!el) return;
    const current = el.innerHTML;
    if (current !== valueHtml) {
      el.innerHTML = valueHtml || "";
    }
  }, [valueHtml]);

  useEffect(() => { syncFromProp(); }, [syncFromProp]);

  const emitChange = useCallback(() => {
    const el = elRef.current; if (!el) return;
    const clean = sanitizeHtml(el.innerHTML);
    if (clean !== valueHtml) {
      onChangeHtml?.(clean);
    }
  }, [onChangeHtml, valueHtml]);

  // Schedule an emit after DOM mutations (paste/cut/drop/composition)
  const scheduleEmitChange = useCallback(() => {
    // microtask to ensure DOM is updated
    setTimeout(() => emitChange(), 0);
  }, [emitChange]);

  const focus = useCallback(() => {
    elRef.current?.focus();
  }, []);

  const exec = useCallback((action, value) => {
    const el = elRef.current; if (!el) return;
    el.focus();

    // Helpers for toggle-aware formatting
    const findAncestorWithin = (start, root, predicate) => {
      let n = start;
      while (n && n !== root) {
        if (predicate(n)) return n;
        n = n.parentNode;
      }
      return null;
    };
    const unwrapElement = (element) => {
      if (!element || !element.parentNode) return;
      const parent = element.parentNode;
      const selection = window.getSelection?.();
      let first = null; let last = null;
      while (element.firstChild) {
        const child = element.firstChild;
        parent.insertBefore(child, element);
        if (!first) first = child;
        last = child;
      }
      parent.removeChild(element);
      // Try to restore a sensible selection around unwrapped content
      if (selection && first) {
        try {
          const range = document.createRange();
          range.setStartBefore(first);
          range.setEndAfter(last || first);
          selection.removeAllRanges();
          selection.addRange(range);
        } catch (_) { /* ignore */ }
      }
    };
    // execCommand is deprecated and absent in some environments (e.g. jsdom).
    // Insert at the caret when the selection is inside the editor, otherwise
    // append at the end.
    const insertNodeAtSelection = (node) => {
      const selection = window.getSelection?.();
      let range = null;
      if (selection && selection.rangeCount > 0) {
        const candidate = selection.getRangeAt(0);
        if (el.contains(candidate.commonAncestorContainer)) {
          range = candidate;
        }
      }
      if (range) {
        range.deleteContents();
        range.insertNode(node);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        el.appendChild(node);
      }
    };
    const tryExecCommand = (command, commandValue) => {
      try {
        if (typeof document.execCommand !== 'function') return false;
        return document.execCommand(command, false, commandValue);
      } catch (_) {
        return false;
      }
    };

    try {
      switch (action) {
        case 'bold': document.execCommand('bold'); break;
        case 'italic': document.execCommand('italic'); break;
        case 'strike': document.execCommand('strikeThrough'); break;
        case 'ol': document.execCommand('insertOrderedList'); break;
        case 'ul': document.execCommand('insertUnorderedList'); break;
        case 'quote': {
          const sel = window.getSelection && window.getSelection();
          if (sel && sel.rangeCount) {
            const range = sel.getRangeAt(0);
            let node = range.commonAncestorContainer;
            if (node && node.nodeType === Node.TEXT_NODE) node = node.parentNode;
            const bq = findAncestorWithin(node, el, (n) => n.nodeType === Node.ELEMENT_NODE && n.tagName?.toLowerCase() === 'blockquote');
            if (bq) {
              // Toggle OFF -> unwrap
              unwrapElement(bq);
              break;
            }
          }
          // Toggle ON -> apply blockquote
          document.execCommand('formatBlock', false, 'blockquote');
          break;
        }
        case 'code': {
          const sel = window.getSelection && window.getSelection();
          const selectionText = sel && sel.toString ? sel.toString() : '';
          const hasNewline = Boolean(selectionText && selectionText.includes('\n'));

          let container = sel && sel.rangeCount ? sel.getRangeAt(0).commonAncestorContainer : null;
          if (container && container.nodeType === Node.TEXT_NODE) container = container.parentNode;
          const inPre = container ? findAncestorWithin(container, el, (n) => n.nodeType === Node.ELEMENT_NODE && n.tagName?.toLowerCase() === 'pre') : null;
          const inCode = container ? findAncestorWithin(container, el, (n) => n.nodeType === Node.ELEMENT_NODE && n.tagName?.toLowerCase() === 'code') : null;

          // First: if selection intersects any <code> or <pre> nodes at all, unwrap them (true toggle-off)
          try {
            if (sel && sel.rangeCount) {
              const range = sel.getRangeAt(0);
              const nodesToUnwrap = [];
              const walker = document.createTreeWalker(el, NodeFilter.SHOW_ELEMENT, {
                acceptNode: (node) => {
                  if (!range || !node || node === el) return NodeFilter.FILTER_SKIP;
                  const tag = node.tagName?.toLowerCase?.();
                  if (tag === 'code' || tag === 'pre') {
                    try { if (range.intersectsNode(node)) return NodeFilter.FILTER_ACCEPT; } catch (_) { /* Safari can throw for detached nodes */ }
                  }
                  return NodeFilter.FILTER_SKIP;
                }
              });
              let cur = walker.currentNode;
              // TreeWalker starts at root; advance to first
              cur = walker.nextNode();
              while (cur) {
                nodesToUnwrap.push(cur);
                cur = walker.nextNode();
              }
              if (nodesToUnwrap.length) {
                nodesToUnwrap.forEach((n) => unwrapElement(n));
                break; // toggled OFF
              }
            }
          } catch (_) { /* ignore */ }

          // If inside <pre>, toggle OFF by unwrapping it regardless of selection text
          if (inPre) { unwrapElement(inPre); break; }

          if (hasNewline) {
            // Multi-line selection -> toggle <pre>
            document.execCommand('formatBlock', false, 'pre');
            break;
          }

          // Inline code: if already in <code>, unwrap; else wrap
          if (inCode) {
            unwrapElement(inCode);
          } else if (sel && sel.rangeCount) {
            try {
              const range = sel.getRangeAt(0);
              const wrapper = document.createElement('code');
              range.surroundContents(wrapper);
            } catch (_) {
              // Fallback: insert sanitized HTML for current selection
              const safeInner = sanitizeHtml(selectionText || '');
              document.execCommand('insertHTML', false, `<code>${safeInner}</code>`);
            }
          }
          break;
        }
        case 'link': {
          // eslint-disable-next-line no-alert
          const url = window.prompt('Enter URL (https://…)', 'https://');
          if (!url) break;
          document.execCommand('createLink', false, url);
          break;
        }
        case 'text': {
          const v = String(value ?? '');
          if (!tryExecCommand('insertText', v)) {
            insertNodeAtSelection(document.createTextNode(v));
          }
          break;
        }
        case 'html': {
          const safe = sanitizeHtml(String(value ?? ''));
          if (!tryExecCommand('insertHTML', safe)) {
            insertNodeAtSelection(document.createRange().createContextualFragment(safe));
          }
          break;
        }
        default:
          break;
      }
    } catch (_) {
      // ignore
    }
    // Sanitize any inserted markup
    emitChange();
  }, [emitChange]);

  const handleFocus = useCallback((event) => {
    onFocus?.(event);
  }, [onFocus]);

  const handleBlur = useCallback((event) => {
    emitChange();
    onBlur?.(event);
  }, [emitChange, onBlur]);

  const handleKeyUp = useCallback((event) => {
    onKeyUp?.(event);
    emitChange();
  }, [onKeyUp, emitChange]);

  const handleBeforeInput = useCallback(() => {
    scheduleEmitChange();
  }, [scheduleEmitChange]);

  const handlePaste = useCallback((event) => {
    onPaste?.(event);
    scheduleEmitChange();
  }, [onPaste, scheduleEmitChange]);

  const handleCut = useCallback(() => {
    scheduleEmitChange();
  }, [scheduleEmitChange]);

  const handleDrop = useCallback(() => {
    scheduleEmitChange();
  }, [scheduleEmitChange]);

  const handleCompositionEnd = useCallback(() => {
    // After IME composition, ensure we read the final text
    scheduleEmitChange();
  }, [scheduleEmitChange]);

  useImperativeHandle(ref, () => ({
    focus,
    exec,
    getHtml: () => sanitizeHtml(elRef.current?.innerHTML || ''),
    getElement: () => elRef.current,
  }));

  return (
    <div
      ref={elRef}
      className={className}
      contentEditable={!disabled}
      role="textbox"
      aria-multiline="true"
      aria-label={ariaLabel}
      data-placeholder={placeholder}
      onInput={emitChange}
      onBeforeInput={handleBeforeInput}
      onPaste={handlePaste}
      onCut={handleCut}
      onDrop={handleDrop}
      onCompositionEnd={handleCompositionEnd}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onKeyUp={handleKeyUp}
      style={{ whiteSpace: 'pre-wrap', outline: 'none' }}
      suppressContentEditableWarning
    />
  );
});

RichTextInput.propTypes = {
  valueHtml: PropTypes.string,
  onChangeHtml: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  onFocus: PropTypes.func,
  onClick: PropTypes.func,
  onBlur: PropTypes.func,
  onPaste: PropTypes.func,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default RichTextInput;
